import _ from 'lodash';
import Emitter from '../emitter';
import CONSTANT from '../../constant';
import Config from '../../config';
import mElectronApi from '../../util/electron';
import { forceDownload, openProjectTarget, getFileSuffix, readAsArrayBuffer } from '../../util';
import support from '../../support';
import { getDefaultLang } from '../../locales';
import authorize from '../authorize';

import services from './service/index';
import projectModel from '../../model/project.model';

export const mscratch = new Mscratch({
    lang: getDefaultLang(),
    resource: {
        // 系统静态资源目录
        scratch_path: './static/resource/scratch/',
        // 用户本地资源目录
        user_path: Config.userSpriteBasepath
    },
    enableDevice: Config.enableDevice
});

class MScratch {

    constructor() {
        this.logger = mscratch.logger;
        this.progress = null;
        this.project = mscratch.project;
    }

    inject(domId, projectData) {
        const result = mscratch.inject(domId, projectData);

        mscratch.onDidMount(function () {
            console.log('---mscratch did mount---');
            Emitter.emit('MscratchDidMount');
        }, true);

        mscratch.project.onProjectStartLoad(() => {
            Emitter.emit('ProjectStartLoad');
        });

        mscratch.project.onProjectEndLoad(() => {
            Emitter.emit('ProjectEndLoad');
        });

        // 监听注入并渲染完成后，执行回调
        mscratch.onReady(() => {
            // 注册 API服务
            for (const key in services) {
                mscratch.registService(services[key]);
            }

            //TODO @zjm 应该是每个设备单独设置把？全局设置导致后续设备连接扩展性变差
            mscratch.transporter.setSender((buff) => {
                Emitter.emit('SetSender', buff);
            });

            // 连接后告知 IDE 改变按钮状态
            mscratch.connection.onConnect((device) => {
                Emitter.emit('ClickConnect', device);
            });
            // 断开连接后告知 IDE 改变按钮状态
            mscratch.connection.onDisconnect((device) => {
                Emitter.emit('ClickDisconnect', device);
            });

            // 添加了设备角色
            mscratch.extensions.onDeviceSpriteAdd((device) => {
                Emitter.emit('DeviceSpriteAdd', device);
            });
            // 移除了设备角色
            mscratch.extensions.onDeviceSpriteDelete((device) => {
                Emitter.emit('DeviceSpriteDelete', device);
            });
            // 点击扩展列表
            mscratch.layout.onOpenExtensionsBtnClicked(() => {
                Emitter.emit('ClickExtList');
            });
            // 点击设备列表
            mscratch.layout.onOpenDevicesBtnClicked(() => {
                Emitter.emit('ClickDeviceList');
            });
            // 角色列表按钮被点击，事件监听
            mscratch.layout.onOpenSpriteLibraryBtnClicked(() => {
                Emitter.emit('ClickedOpenSprite', CONSTANT.SPRITE_TYPE.LIBS);
            });
            // 造型列表按钮被点击，事件监听
            mscratch.layout.onOpenCostumeLibraryBtnClicked(() => {
                Emitter.emit('ClickedOpenSprite', CONSTANT.SPRITE_TYPE.COSTUMES);
            });
            // 背景列表按钮被点击，事件监听
            mscratch.layout.onOpenBackdropLibraryBtnClicked(() => {
                Emitter.emit('ClickedOpenSprite', CONSTANT.SPRITE_TYPE.BACKDROPS);
            });
            // 声音列表按钮被点击，事件监听
            mscratch.layout.onOpenSoundLibraryBtnClicked(() => {
                Emitter.emit('ClickedOpenSprite', CONSTANT.SPRITE_TYPE.SOUNDS);
            });
            // 点击录音保存按钮
            mscratch.vm.onSoundRecorded(sound => {
                Emitter.emit('ClickRecordSound', sound);
            });

            mscratch.extensions.addUpdateListener((extId) => {
                Emitter.emit('LoadedExt', extId);
            }, (extId) => {
                Emitter.emit('UnloadedExt', extId);
            });

            // 恢复出厂设置
            mscratch.connection.onResetDevice((device) => {
                Emitter.emit('ClickResetDevice', device);
            });
            // 点击校正颜色传感器
            mscratch.connection.onCalibrateColorSensor((device) => {
                Emitter.emit('ClickCalibrateColor', device);
            });
            // 点击校正陀螺仪
            mscratch.connection.onCalibratGyro((device) => {
                Emitter.emit('ClickCalibratGyro', device);
            });
            // 侧边栏块点击触发的操作
            mscratch.workspace.onFlyoutBlockClicked((block) => {
                Emitter.emit('FlyoutBlockClicked', block);
            });
            // 点击工作区
            mscratch.scratchEvents.onWorkspaceClicked(() => {
                Emitter.emit('CanHideOpenedMenu');
            });
            // 点击撤销或重做
            mscratch.workspace.onUndo((data) => {
                Emitter.emit('SetUndoRedoOperate', data);
            });

            for (let key in support.drivers) {
                mscratch.extensions.registerFirmwareDriver(key, support.drivers[key]);
            }
            console.log('support : ', support);
            for (let key in support.upload.middlewares) {
                mscratch.extensions.registerUploadMiddleware(key, support.upload.middlewares[key]);
            }
            Emitter.emit('MscratchReady');
        });
        return result;
    }

    /**
     * 导入
     *
     * @param {any} [file=null]
     * @returns
     */
    async import(file = null, opts) {
        if (mElectronApi.has('file')) {
            return mElectronApi.read(file, opts);
        } else {
            if (!(file instanceof File)) {
                file = await openProjectTarget(opts);
            }
            return this.useWebRead(file);
        }
    }

    /**
     * 获取项目json
     */
    async getProjectJson() {
        let data = {
            channelId: authorize.getChannelId(),
            version: Config.appVersion,
            createdAt: new Date().getTime()
        };
        window._baseProjInfo = data;
        return await mscratch.project.getProjectJson([
            {
                name: 'mblock5',
                data
            }
        ]);
    }

    getProjectJsonString() {
        return mscratch.project.getProjectJsonString();
    }

    /**
     * 获取项目封面图
     */
    getProjectCoverPic() {
        return mscratch.stage.getCoverImage();
    }

    /**
     * 渲染项目数据
     *
     * @author LiHuan
     * @param {any} data
     * @returns
     */
    renderProject(data, successCallback) {
        projectModel.setProjectPlatformType();
        if (_.isArrayBuffer(data)) {
            return mscratch.project.load(data, successCallback);
        }
        // 兼容客户端读的数据
        if (_.isString(data)) {
            try {
                data = JSON.parse(data);
            } catch (error) {
                console.warn(error);
            }
        }
        if (_.isObject(data)) {
            if (data.isBuffer) {
                return mscratch.project.load(data.buffer, successCallback);
            }
            const ideData = data.ide || data;
            const extData = _.has(data, 'ai') ? {
                deeplearning: data.ai
            } : null;
            return mscratch.project.renderProjectJson(ideData, extData, successCallback);
        }
        return null;
    }

    /**
     * 使用 web 方式读文件
     *
     * @author LiHuan
     * @param {any} file
     */
    useWebRead(file) {
        if (file instanceof File) {
            const suffix = getFileSuffix(file.name);
            const isScratchProject = ['sb2', 'sb3'].indexOf(suffix) >= 0;
            const isMblock = suffix === 'mblock';
            let isloaded = false;
            let isBuffer = false;
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    let data = reader.result;
                    isloaded = true;
                    if (isMblock && _.isString(data)) {
                        try {
                            data = JSON.parse(data);
                        } catch (error) {
                            isloaded = false;
                            isBuffer = true;
                            reader.readAsArrayBuffer(file);
                        }
                    }
                    if (isloaded) {
                        if (isBuffer) {
                            data = { isBuffer, buffer: data };
                        }
                        resolve({ data, filePath: file.name });
                    }
                };
                reader.onerror = (err) => {
                    reject(err);
                };
                if (isScratchProject) {
                    reader.readAsArrayBuffer(file);
                } else {
                    reader.readAsText(file);
                }
            });
        }
    }

    /**
     * 导出，保存
     *
     * @param {boolean} [isSaveas=false]
     * @returns
     */
    async export(filePath, opts, project) {
        let data = await this.getProjectJson();
        if (mElectronApi.has('file')) {
            data = await readAsArrayBuffer(data);
            return mElectronApi.save(data, filePath, opts);
        } else {
            const filePath = `${project.name}.${CONSTANT.PROJECT_SUFFIX[0]}`;
            forceDownload(data, filePath);
        }
    }

    /**
     * 撤销
     *
     * @author LiHuan
     */
    undo() {
        mscratch.workspace.undo();
    }

    /**
     * 重做
     *
     * @author LiHuan
     */
    redo() {
        mscratch.workspace.redo();
    }

    /**
     * 切换语言
     *
     * @author LiHuan
     * @param {any} lang
     */
    changeLanguage(lang) {
        mscratch.setLang(lang);
    }

    addLocaleData(lang, msgs, localeData) {
        mscratch.addLocaleData(lang, msgs, localeData);
    }

    /**
     * 重新设置大小
     *
     * @author LiHuan
     */
    resize() {
        // console.log('resize');
        mscratch.svgResize();
    }

    /**
     * 回发数据
     *
     * @author LiHuan
     * @param {any} buff
     */
    doReceived(buff) {
        mscratch.transporter.doReceived(buff);
    }

    /**
     * 启用块
     *
     * @author LiHuan
     * @param {any} types
     */
    enableBlocks(types) {
        if (mscratch && mscratch.workspace) {
            mscratch.workspace.disableBlocks(types, false);
        }
    }

    /**
     * 禁用块操作
     *
     * @author LiHuan
     * @param {any} types
     */
    disableBlocks(types) {
        if (mscratch && mscratch.workspace) {
            mscratch.workspace.disableBlocks(types, true);
        }
    }

    /**
     * 重置工作区
     *
     * @author LiHuan
     */
    reset() {
        mscratch.workspace.reset();
    }

    /**
     * 清空项目
     *
     * @author LiHuan
     */
    clear() {
        mscratch.project.reset();
    }

    /**
     * 获取工作内容
     *
     * @author LiHuan
     * @returns
     */
    getCodeContent() {
        return mscratch.code.getContent();
    }

    getAllExtensionIds() {
        if (mscratch.extensions) {
            return mscratch.extensions.getAllExtensionIds();
        }
        return null;
    }

    /**
     * 设置工作区内容
     *
     * @author LiHuan
     * @param {any} code
     */
    setCodeContent(code) {
        mscratch.codey.setContent(code);
    }

    /**
     * 发送 DTR 握手协议
     *
     * @author LiHuan
     */
    handshakeDTR() {
        mscratch.codey.handshakeDTR().then(res => {
            if (res.err) {
                // 握手失败
            } else if (res.status === 20) {
                // 握手成功，可以拉取 dtr
            }
        });
    }

    addSprite(data) {
        return mscratch.vm.addSprite(data);
    }

    addCostume(data) {
        return mscratch.vm.addCostume(data);
    }

    addBackdrop(data) {
        return mscratch.vm.addBackdrop(data);
    }

    addSound(data) {
        return mscratch.vm.addSound(data);
    }

    openRecordSound() {
        return mscratch.layout.openSoundRecorder();
    }

    uploadRetry() {
        mscratch.uploader.uploadCode();
    }

    clearUpWorkspace() {
        // TODO: 暂时移除整理功能，用重写示例来代替
        // mscratch.workspace.resetWhenFirstSelectSprite();
    }

    clearLayout() {
        mscratch.layout.soundEditorHide();
        mscratch.layout.costumeEditorHide();
        mscratch.layout.blocklyChaffHide();
    }

    stop() {
        mscratch.vm.stop();
    }

    costumeEditorShow() {
        mscratch.layout.costumeEditorShow();
        mscratch.layout.soundEditorHide();
    }

    setTransporterRawPort(port) {
        mscratch.transporter.setRawPort(port);
    }

    isCostumeEditorOpened() {
        return mscratch.layout && mscratch.layout.isCostumeEditorOpened();
    }

    isSoundEditorOpened() {
        return mscratch.layout && mscratch.layout.isSoundEditorOpened();
    }

    uploadFirmware(data) {
        mscratch.uploader.uploadFirmware(data);
    }

    switchTransportChannel(channel) {
        mscratch.transporter.switchChannel(channel);
    }

    setBlocklyChaffHide() {
        return mscratch.layout && mscratch.layout.blocklyChaffHide();
    }
}

export default new MScratch();